<script setup lang="ts">
import type { IInvestorAnalysisData } from '@types'
import UserAdapter from '@adapters/UserAdapter' // Adjust import path as needed
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import { showToast } from 'vant'
import { ref, watch } from 'vue'
import { investmentGoalOptions, investorTypeOptions } from '~/consts/investorPreferencesConsts'
import { useAuthStore } from '~/stores/auth'

interface Props {
  isOpen: boolean
  user?: IUser | null
}

const props = defineProps({
  isOpen: { type: Boolean },
  user: null
})
const emit = defineEmits(["update:isOpen"])

const authStore = useAuthStore()

// Form state
const showSheet = ref(false)
const type_of_investor = ref('')
const investment_goals = ref<Array<string>>([])

// Watch for prop changes
watch(() => props.isOpen, (val) => {
  showSheet.value = val
  if (val && props.user) {
    type_of_investor.value = props.user.investor_preferences?.type_of_investor
    investment_goals.value = props.user.investor_preferences?.investment_goals
  }
  else if (val) {
    type_of_investor.value = ''
    investment_goals.value = []
  }
})

// Sync internal state with prop
watch(showSheet, (val) => {
  emit('update:isOpen', val)
})

// Save handler
async function handleSave() {
  const data: IInvestorAnalysisData = {
    investment_goals: investment_goals.value,
    type_of_investor: type_of_investor.value,
  }

  await UserAdapter.updateUserInvestorData(data)
  authStore.loadUser()

  showToast({
    message: 'Updated',
    type: 'success',
    duration: 2000,
  })
  showSheet.value = false
}
</script>

<template>
  <van-action-sheet
    v-model:show="showSheet"
    title="Investor investment profile"
  >
    <div class="px-6 py-8 flex flex-col gap-6">
      <!-- Avatar -->

      <p class="text-center text-sm text-gray-500 capitalize -mt-2">
        Help us understand what kind of investor you are and your goals so our AI assistant may best serve you.
      </p>

      <div class=" text-md font-semibold -mb-2">
        What type of investor are you?
      </div>

      <section>
        <div v-for="option of investorTypeOptions" :key="option.value" class="flex items-center gap-2 mb-2">
          <RadioButton v-model="type_of_investor" :input-id="option.value" name="category" :value="option.value" />
          <label class="text-[15px] leading-[18px]" :for="option.value">{{ option.label }}</label>
        </div>
      </section>

      <div class=" text-md font-semibold -mb-2">
        What are your investment goals?
      </div>
      <section>
        <div v-for="option of investmentGoalOptions" :key="option.value" class="flex items-center gap-2 mb-2">
          <Checkbox v-model="investment_goals" :input-id="option.value" name="category" :value="option.value" />
          <label class="text-[15px] leading-[18px]" :for="option.value">{{ option.label }}</label>
        </div>
      </section>

      <div class="flex flex-col items-center">
        <ButtonComponent label="Save account" class="w-[180px]" @click="handleSave" />
      </div>
    </div>
  </van-action-sheet>
</template>

<style scoped>
/* Keep it minimal - rely on Vant's built-in styles */
</style>
